import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Header } from "../components/portfolio/Header";
import { Footer } from "../components/portfolio/Footer";
import { fetchPost } from "../lib/apiClient";
import { PROFILE } from "../data";

export default function BlogPostPage() {
  const { slug } = useParams();
  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["post", slug],
    queryFn: () => fetchPost(slug),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) document.title = `${post.title} — ${PROFILE.name}`;
  }, [post]);

  return (
    <div data-testid="blog-post-page" className="bg-white min-h-screen">
      <Header />
      <article className="pt-32 pb-24 lg:pt-40">
        <div className="mx-auto max-w-3xl section-x">
          <Link
            to="/"
            data-testid="back-to-home"
            onClick={() => setTimeout(() => document.getElementById("journal")?.scrollIntoView(), 100)}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-berry transition-colors duration-200 mb-10"
          >
            <ArrowLeft size={16} /> Back to journal
          </Link>

          {isLoading && (
            <div className="flex items-center gap-3 text-slate-400 py-20">
              <Loader2 className="animate-spin" size={20} /> Loading article...
            </div>
          )}

          {isError && (
            <div data-testid="post-not-found" className="py-20">
              <h1 className="font-serif text-3xl text-navy">Article not found</h1>
              <Link to="/" className="text-berry mt-4 inline-block">Return home</Link>
            </div>
          )}

          {post && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="label-caps text-berry">{post.category}</span>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-navy leading-tight mt-4">
                {post.title}
              </h1>
              <div className="mt-6 flex items-center gap-3 text-sm text-slate-400 border-b border-slate-200 pb-8">
                <span>{new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full" />
                <span>{post.read_time}</span>
              </div>
              <div className="mt-10 prose-content text-lg text-slate-700 leading-relaxed space-y-6">
                {post.body.split("\n\n").map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </article>
      <Footer />
    </div>
  );
}
